import { FC, useMemo } from "react";
import { Column } from "react-table";
import { Table } from "common/components/Table/Table";
import { QueryLayout } from "common/components/Layout/QueryLayout";
import { SkeletonTable } from "common/components/Table/SkeletonTable";
import { NotFoundTitle } from "common/components/Title/NotFoundTitle";
import { CustomLineChart } from "common/components/Chart/LineChart";
import { useGetFinanceSummaryQuery } from "services/admin/adminReportsApi";
import { IFinanceSummaryItem } from "common/models/admin/IReports";
import {
  formatMoney,
  getQueryDateParam,
  timeFormatter,
} from "common/utils/helpers";

type Metric = "revenue" | "expenses" | "profit";

const METRIC_LABELS: Record<Metric, string> = {
  revenue: "Выручка (оплачено клиентами), руб.",
  expenses: "Расходы (вознаграждения исполнителей), руб.",
  profit: "Прибыль (комиссия площадки), руб.",
};

// Сводка по завершённым заказам за день. График строится по выбранной метрике,
// таблица показывает все три и итог за период
export const FinanceSummaryTable: FC<{
  metric: Metric;
  dateFrom?: string;
  dateTo?: string;
}> = ({ metric, dateFrom, dateTo }) => {
  const { data, isLoading, isFetching, error } = useGetFinanceSummaryQuery({
    date_from: getQueryDateParam(dateFrom),
    date_to: getQueryDateParam(dateTo),
  });

  const chartData = useMemo(
    () =>
      data?.items.map((item) => ({ date: item.date, value: item[metric] })) ??
      [],
    [data, metric]
  );

  const columns = useMemo(() => buildColumns(metric), [metric]);

  return (
    <QueryLayout
      isLoading={isLoading}
      error={error}
      skeleton={<SkeletonTable rowCount={6} colCount={columns.length} />}
    >
      {data?.items.length ? (
        <>
          {chartData.length > 1 && (
            <div className="mb-[20px]">
              <CustomLineChart chartData={chartData} />
            </div>
          )}
          <div className="mb-[10px] text-sm font-semibold text-black">
            {METRIC_LABELS[metric]}: {formatMoney(data.totals[metric])} за
            период
          </div>
          <Table data={data.items} columns={columns} isLoading={isFetching} />
        </>
      ) : (
        <NotFoundTitle />
      )}
    </QueryLayout>
  );
};

const sumColumn =
  (key: keyof IFinanceSummaryItem, money = true) =>
  (info: any) => {
    const total = info.rows.reduce(
      (sum: number, row: any) => sum + Number(row.original[key] ?? 0),
      0
    );
    return <>{money ? formatMoney(total) : total}</>;
  };

function buildColumns(metric: Metric): Column<IFinanceSummaryItem>[] {
  const highlight = (key: Metric) =>
    key === metric ? "font-semibold text-primary" : "";

  return [
    {
      Header: "Дата",
      accessor: "date",
      Footer: "Итого",
      Cell: ({ row }: any) => (
        <span className="whitespace-nowrap">
          {timeFormatter({
            time: new Date(row.original.date),
            mode: "dd-Mth-yyyy",
          })}
        </span>
      ),
    },
    {
      Header: "Завершённые заказы",
      accessor: "orders_count",
      Footer: sumColumn("orders_count", false),
    },
    {
      Header: "Выручка, руб.",
      accessor: "revenue",
      Footer: sumColumn("revenue"),
      Cell: ({ row }: any) => (
        <span className={highlight("revenue")}>
          {formatMoney(row.original.revenue)}
        </span>
      ),
    },
    {
      Header: "Расходы, руб.",
      accessor: "expenses",
      Footer: sumColumn("expenses"),
      Cell: ({ row }: any) => (
        <span className={highlight("expenses")}>
          {formatMoney(row.original.expenses)}
        </span>
      ),
    },
    {
      Header: "Прибыль, руб.",
      accessor: "profit",
      Footer: sumColumn("profit"),
      Cell: ({ row }: any) => (
        <span
          className={`${highlight("profit")} ${
            row.original.profit < 0 ? "text-red" : ""
          }`}
        >
          {formatMoney(row.original.profit)}
        </span>
      ),
    },
  ];
}
